// Angular
import { DOCUMENT, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CanActivateFn, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
// Constants
import { APP_CONSTANT, COUNTRY_LIST, ENV_CUSTOMER_LANG, LOCALES } from '@constants';
// RxJS
import { delay, map } from 'rxjs/operators';
// Transloco
import { TranslocoService } from '@jsverse/transloco';
// Services
import { CommonService } from '@core/utils';
import { AppInitLoaderService } from './app-init-loader.service';
import { LayoutService } from './views/pages/layout/services';

export const LangRouteGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  // Inject services
  const router = inject(Router);
  const translocoService = inject(TranslocoService);
  const commonService = inject(CommonService);
  const layoutService = inject(LayoutService);
  const appInitLoaderService = inject(AppInitLoaderService);
  const platformId = inject<Object>(PLATFORM_ID);
  const document = inject<Document>(DOCUMENT);

  try {
    return appInitLoaderService.appInitDoneSubject.pipe(
      map(() => {
        const availableLangs: Array<string> = translocoService.getAvailableLangs() as Array<string>;
        const lang = route.paramMap.get('lang');

        if (lang && availableLangs.includes(lang)) {
          translocoService.setActiveLang(lang);

          // Save the preference in a cookie (Browser only)
          if (isPlatformBrowser(platformId)) {
            const expiry = new Date();
            expiry.setFullYear(expiry.getFullYear() + 1); // Sets a cookie valid for 1 year
            document.cookie = `selected-lang=${lang}; expires=${expiry.toUTCString()}; path=/`;
          }

          const savedLanguage = commonService.getFromLocalStorage(ENV_CUSTOMER_LANG);

          if (lang !== savedLanguage) {
            const LOCALE_MAP = new Map(LOCALES.map(locale => [locale.locale, locale]));
            const COUNTRY_MAP = new Map(COUNTRY_LIST.map(country => [String(country.id), country]));

            const locale = LOCALE_MAP.get(lang);

            if (locale) {
              const country = COUNTRY_MAP.get(String(locale.id));

              layoutService.setCustomerLanguage(locale);
              layoutService.setCustomerCountry(country);

              if (country?.currency) {
                layoutService.setCustomerCurrency(country.currency);
              } else {
                console.warn(`Currency not found for country with ID: ${locale.id}`);
                layoutService.setCustomerCurrency('');
              }
            } else {
              console.warn(`Locale not found for language code: ${lang}`);
            }
          }
        } else {
          handleInvalidLanguage(router, commonService, platformId);
        }

        return true;
      }),
      delay(200)
    );
  } catch (error) {
    console.log('lang-canactivate-route-error', error);
    return true;
  }
};

/**
 * Handle Invalid Language Route
 * @private
 */
const handleInvalidLanguage = (router: Router, commonService: CommonService, platformId: Object): void => {
  try {
    if (isPlatformBrowser(platformId)) {
      const language = commonService.getFromLocalStorage(ENV_CUSTOMER_LANG);

      if (language && language !== APP_CONSTANT.DFAULT_LOCALE) {
        router.navigateByUrl(`/${language}/refresh`, { skipLocationChange: true }).then(() => {
          window.location.href = `/${language}`;
        });
      } else {
        router.navigateByUrl(`/${APP_CONSTANT.DFAULT_LOCALE}`);
      }
    } else {
      router.navigateByUrl(`/${APP_CONSTANT.DFAULT_LOCALE}`);
    }
  } catch (error) {
    console.log('lang-route-error', error);
  }
};
